import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Anchor, ShipWheel } from "lucide-react";
import { HeroPhotoPicker } from "@/components/HeroPhotoPicker";

const DEFAULT_HEROES = {
    powerboat: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1600&q=70",
    sailboat: "https://images.unsplash.com/photo-1500627964684-141351970a7f?w=1600&q=70",
    fishing: "https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=1600&q=70",
};

export default function Onboarding() {
    const nav = useNavigate();
    const [presets, setPresets] = useState([]);
    const [vesselType, setVesselType] = useState("powerboat");
    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    const [tagline, setTagline] = useState("Maintenance · Costs · Fuel · Parts");
    const [heroUrl, setHeroUrl] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/presets").then((r) => setPresets(r.data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/vessels", {
                name,
                vessel_type: vesselType,
                location,
                tagline,
                hero_photo_url: heroUrl || DEFAULT_HEROES[vesselType],
            });
            nav(`/app/${data.id}`, { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="onboarding-page" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "60px 24px" }}>
            <div style={{ width: "100%", maxWidth: 720 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
                    <Anchor size={18} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 22 }}>Bosun's Mate</span>
                </div>

                <h1 className="bm-serif" style={{ fontSize: 36, margin: "0 0 8px", fontWeight: 500 }}>
                    Welcome aboard, captain.
                </h1>
                <p style={{ color: "var(--bm-text-muted)", margin: "0 0 30px", fontSize: 15 }}>
                    Tell us about your first vessel — you can add more later, and you can change anything anytime.
                </p>

                <form onSubmit={submit} className="bm-card" style={{ padding: 28 }} data-testid="onboarding-form">
                    <label className="bm-label">Vessel type</label>
                    <div data-testid="vessel-type-selector" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 20 }}>
                        {presets.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                data-testid={`preset-${p.key}-btn`}
                                onClick={() => setVesselType(p.key)}
                                className="bm-card-flat"
                                style={{
                                    padding: 14,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    border: vesselType === p.key ? "1px solid var(--bm-gold)" : "1px solid var(--bm-border-soft)",
                                    background: vesselType === p.key ? "rgba(212, 181, 114, 0.05)" : undefined,
                                }}
                            >
                                <ShipWheel size={18} color={vesselType === p.key ? "var(--bm-gold)" : "var(--bm-text-muted)"} />
                                <div style={{ marginTop: 8, fontWeight: 600, fontSize: 14 }}>{p.label}</div>
                                <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 4 }}>{p.description}</div>
                            </button>
                        ))}
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label className="bm-label">Vessel name</label>
                        <input data-testid="vessel-name-input" className="bm-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Northern Belle" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                        <div>
                            <label className="bm-label">Home port / location</label>
                            <input data-testid="vessel-location-input" className="bm-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Whitehorse, Yukon" />
                        </div>
                        <div>
                            <label className="bm-label">Tagline (optional)</label>
                            <input data-testid="vessel-tagline-input" className="bm-input" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Maintenance · Costs · Fuel" />
                        </div>
                    </div>
                    <div style={{ marginBottom: 22 }}>
                        <label className="bm-label">Hero photo (optional — we'll pick one if you skip)</label>
                        <HeroPhotoPicker value={heroUrl} onChange={setHeroUrl} />
                    </div>

                    {err && (
                        <div data-testid="onboarding-error" style={{ marginBottom: 14, color: "var(--bm-bad)", fontSize: 13 }}>{err}</div>
                    )}

                    <button data-testid="onboarding-submit-btn" type="submit" disabled={busy} className="bm-btn bm-btn-gold" style={{ padding: "12px 22px", opacity: busy ? 0.6 : 1 }}>
                        {busy ? "Setting the helm…" : "Create my vessel"}
                    </button>
                </form>
            </div>
        </div>
    );
}
